import { api } from '../base';
import { z } from 'zod';

// Storage API response schemas
const StorageUsageSchema = z.object({
  total_bytes: z.number(),
  file_count: z.number(),
  unique_blob_count: z.number(),
  deleted_bytes: z.number(),
  deleted_file_count: z.number(),
});

const ProjectStorageSchema = z.object({
  project_id: z.number(),
  project_name: z.string(),
  total_bytes: z.number(),
  file_count: z.number(),
  unique_blob_count: z.number(),
});

const BucketStorageSchema = z.object({
  bucket_id: z.number(),
  bucket_name: z.string(),
  total_bytes: z.number(),
  file_count: z.number(),
  unique_blob_count: z.number(),
});

const SystemStorageStatsSchema = z.object({
  total_bytes: z.number(),
  total_files: z.number(),
  total_blobs: z.number(),
  total_users: z.number(),
  total_projects: z.number(),
  total_buckets: z.number(),
  deleted_bytes: z.number(),
  disk_free_bytes: z.number().optional(),
});

const StorageStatusSchema = z.object({
  used_bytes: z.number(),
  used_bytes_human: z.string(),
  limit_bytes: z.number().nullable(),
  limit_bytes_human: z.string().nullable(),
  used_percentage: z.number(),
  warning_threshold: z.number(),
  critical_threshold: z.number(),
  is_warning: z.boolean(),
  is_critical: z.boolean(),
  disk_free_bytes: z.number().optional(),
});

export type StorageUsage = z.infer<typeof StorageUsageSchema>;
export type ProjectStorage = z.infer<typeof ProjectStorageSchema>;
export type BucketStorage = z.infer<typeof BucketStorageSchema>;
export type SystemStorageStats = z.infer<typeof SystemStorageStatsSchema>;
export type StorageStatus = z.infer<typeof StorageStatusSchema>;

export class StorageApi {
  /**
   * Get storage usage for the current user
   */
  async getUserStorage(): Promise<StorageUsage> {
    const response = await api.get('/v1/storage/usage/user');
    return StorageUsageSchema.parse(response);
  }

  /**
   * Get storage breakdown by projects for the current user
   */
  async getStorageByProjects(): Promise<ProjectStorage[]> {
    const response = await api.get('/v1/storage/usage/projects');
    return z.array(ProjectStorageSchema).parse(response);
  }

  /**
   * Get storage usage for a specific project
   */
  async getProjectStorage(projectId: number): Promise<ProjectStorage> {
    const response = await api.get(`/v1/storage/usage/project/${projectId}`);
    return ProjectStorageSchema.parse(response);
  }

  /**
   * Get storage usage for a specific bucket
   */
  async getBucketStorage(bucketId: number): Promise<BucketStorage> {
    const response = await api.get(`/v1/storage/usage/bucket/${bucketId}`);
    return BucketStorageSchema.parse(response);
  }

  /**
   * Get system-wide storage statistics (admin only)
   */
  async getSystemStats(): Promise<SystemStorageStats> {
    const response = await api.get('/v1/storage/stats');
    return SystemStorageStatsSchema.parse(response);
  }

  /**
   * Get storage status for the current user
   * Returns usage, limits, and warning/critical states
   */
  async getStorageStatus(): Promise<StorageStatus> {
    const response = await api.get('/v1/storage/status');
    return StorageStatusSchema.parse(response);
  }
}
